import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Box, CircularProgress } from '@mui/material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import CommonHeader from '../../base/views/CommonHeader';
import ApiService from '../../base/api/ApiService';
import { Announcement } from '../../components/models/Announcement';
import apiConfig from '../../apiConfig';

// ?entryPoint=attachment&id=ABC → извлекаем ABC и загружаем через portal API (имеет CORS-заголовки)
async function resolveImages(html: string): Promise<string> {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const images = Array.from(doc.querySelectorAll('img'));

    if (images.length === 0) return doc.body.innerHTML;

    const username = ApiService.getUsername();
    const token = ApiService.getUserData()?.token;
    if (!username || !token) return doc.body.innerHTML;

    const authHeader = btoa(`${username}:${token}`);

    await Promise.all(images.map(async (img) => {
        const src = img.getAttribute('src') ?? '';
        const match = src.match(/[?&]id=([a-f0-9]+)/i);
        if (!match) return;

        const attachmentId = match[1];
        const apiUrl = `${apiConfig.ANNOUNCEMENT_ATTACHMENT}/${attachmentId}`;

        try {
            const response = await fetch(apiUrl, {
                headers: { 'Espo-Authorization': authHeader }
            });
            if (!response.ok) return;
            const data = await response.json();
            if (data.dataUri) {
                img.setAttribute('src', data.dataUri);
            }
        } catch {
            img.removeAttribute('src');
        }
    }));

    return doc.body.innerHTML;
}

const AnnouncementDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [announcement, setAnnouncement] = useState<Announcement | null>(null);
    const [processedHtml, setProcessedHtml] = useState<string>('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) return;
        ApiService.getAnnouncementDetail(id)
            .then(async (data) => {
                setAnnouncement(data);
                if (data.message) {
                    const html = await resolveImages(data.message);
                    setProcessedHtml(html);
                }
                if (!data.isRead) {
                    ApiService.markAnnouncementRead(id).then(() => {
                        window.dispatchEvent(new Event('fcm-message'));
                    });
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
    }

    if (!announcement) {
        return <Typography sx={{ p: 2 }}>Объявление не найдено</Typography>;
    }

    return (
        <div>
            <CommonHeader title={announcement.name} showBackButton={true} />
            <Box sx={{ p: 2 }}>
                {announcement.startDate && (
                    <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                        {format(new Date(announcement.startDate), 'd MMMM yyyy', { locale: ru })}
                    </Typography>
                )}
                <Box
                    dangerouslySetInnerHTML={{ __html: processedHtml }}
                    sx={{ '& img': { maxWidth: '100%', height: 'auto' } }}
                />
            </Box>
        </div>
    );
};

export default AnnouncementDetailPage;
